import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { FC, useEffect } from 'react';
import { Route } from 'react-router';
import { CareMapState } from '../caremap/CareMapState';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { InnerhivePage } from '../InnerhivePage';
import { CareMapNotesList } from './note-list/CareMapNotesList';
import { IonButtons, IonToolbar } from '@ionic/react';
import { AddNoteButton } from './add-note/AddNoteButton';
import { ViewNoteWithQueryParamDrawer } from './view-note/ViewNoteWithQueryParamDrawer';
import { CaremapNotesModelFactory } from './model/CaremapNotesModelFactory';
import { AddNoteWithQueryParamDrawer } from './add-note/AddNoteWithQueryParamDrawer';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareTabsProvider } from '../care/CareTabsProvider';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { AddCareMapButton } from '../caremap/AddCareMapButton';

export const NotesPageRouteHandler: React.FC = () => (
    <Route exact path="/notes/:caremapId" render={routeParams => (
        <CareMapNotesPage careMapId={routeParams.match.params.caremapId} />
    )} />
);

export const CareMapNotesPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <ActiveCaremapNotesPage />
        </ActiveCareMapProviderV2>
    );
};

export const ActiveCaremapNotesPage: React.FC = () => {
    const mixpanel = useInstance(MixPanelEventEmitter);

    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);
    const addNoteDrawer = useInstance(AddNoteWithQueryParamDrawer);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    const tabsProvider = useInstance(CareTabsProvider);

    useEffect(() => {
        mixpanel.track('Notes Page Opened');
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const activeCaremap = careMapState.getActiveCaremap();
    if (!activeCaremap){
        return null;
    }

    const incompleteCareMap = careMapState.getIncompleteCareMap();

    const notes = notesModelFactory.forActiveCaremap().getNotes();

    const canEdit = activeCaremap.links.self.actions.includes('edit-notes');

    const editNote = (noteId: string) => {
        viewNoteDrawer.open({ noteId });
    };

    const addNote = () => {
        addNoteDrawer.open();
    };

    const canAddNotes = careMapState.hasPermissionTo('post-note');

    return (
        <InnerhivePage title="Care" tabs={tabsProvider.getTabs()}>
            <IonToolbar style={{'--padding-bottom': '20px'}}>
                {incompleteCareMap ? (
                    <AddCareMapButton />
                ): (
                    <>
                        <div style={{margin: '4px 12px 0 4px'}}>
                            <CareRecipientSwitcher />
                        </div>
                        {canAddNotes && (
                            <IonButtons slot="end">
                                <AddNoteButton
                                    useIcon
                                    canAdd={canEdit}
                                    onClick={addNote}
                                />
                            </IonButtons>
                        )}
                    </>
                )}
            </IonToolbar>

            {notesModelFactory.forActiveCaremap().hasLoaded && (
                notes.length || canEdit ? (
                    <CareMapNotesList
                        notes={notes}
                        showHeading={false}
                        showNoteTargetHeader
                        onNoteClicked={editNote}
                        onAddNote={addNote}
                    />
                ) : (
                    <div data-type="viewer-no-notes">
                        No public notes yet… Check back soon!
                    </div>
                )
            )}
        </InnerhivePage>
    );
};
